import { ContentTypes, ContentTypesTemplates } from '../../store/common/constants';

export interface UserPrivilegesGetter {
    getCurrentUserPrivileges: () => string[];
}

export enum FeatureTypesWithCurs {
    PREVIEW_CONTENT,
    ANNOTATE_CONTENT,
    ITEM_REVIEW_METADATA,
    METADATA,
    ITEM_REVIEW_ADDITIONAL_RESOURCES,
    CONTENT_BLOCK_AUDIO_SCRIPT,
    ITEM_REVIEW_STATES,
    ATC_CONTENT_BLOCKS_USAGE_LISTS,
    CONTENT_BLOCK_EDIT,
    EDIT_STATUS,
    INTERACTION_BLOCK,
    CREATE_CONTENT,
    CONTENT_BLOCK_READ,
    QUESTION_SET_DESIGN,
    WRITE_RESHARE_CONTENT,
    OWNER_SHARE_CONTENT,
    CONTENT_BLOCK_VERSION_HISTORY,
    QUESTION_VERSION_HISTORY,
    CREATE_QUESTIONS_WITHIN_QUESTION_SET,
    PRINT_MANAGER_ACCESS,
    CONTENT_LIST,
    ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS
}

const { SECTION, QUESTION_SET, QUESTION } = ContentTypes;
const { [SECTION]: SECTION_TEMPLATE, [QUESTION]: QUESTION_TEMPLATE, [QUESTION_SET]: QUESTION_SET_TEMPLATE } = ContentTypesTemplates;

export const FeaturePrivilegesWithCurs = {
    [FeatureTypesWithCurs.CONTENT_BLOCK_VERSION_HISTORY]: {
        READ_CONTENT_BLOCK_SHOW_VERSION_HISTORY: {
            [SECTION]: 'read_content_block_show_version_history',
        },
    },
    [FeatureTypesWithCurs.QUESTION_VERSION_HISTORY]: {
        READ_INTERACTION_BLOCK_SHOW_VERSION_HISTORY: {
            [QUESTION]: 'read_interaction_block_show_version_history',
        },
    },
    [FeatureTypesWithCurs.CONTENT_LIST]: {
        READ_CONTENT_LIST: {
            [QUESTION]: 'read_questions_list',
            [SECTION]: 'read_content_block_list',
        },
    },
    // IB-21478. Only SECTION and QUESTION_SET are governed; questions and template variants
    // deliberately have no entry, so they keep their existing Print Manager behaviour.
    [FeatureTypesWithCurs.PRINT_MANAGER_ACCESS]: {
        READ_PRINT_MANAGER_ACCESS: {
            [SECTION]: 'read_content_block_print_manager_access',
            [QUESTION_SET]: 'read_question_set_print_manager_access',
        },
    },
    [FeatureTypesWithCurs.PREVIEW_CONTENT]: {
        READ_PREVIEW_CONTENT: {
            [QUESTION]: 'read_interaction_block',
            [SECTION]: 'read_content_block',
            [QUESTION_SET]: 'read_question_set',
            [SECTION_TEMPLATE]: 'read_content_block_template',
            [QUESTION_TEMPLATE]: 'read_interaction_block_templates',
            [QUESTION_SET_TEMPLATE]: 'read_question_set_template',
        },
    },
    [FeatureTypesWithCurs.ANNOTATE_CONTENT]: {
        WRITE_ANNOTATE: {
            [SECTION]: 'write_content_block_annotate',
            [QUESTION]: 'write_question_annotate',
            [QUESTION_SET]: 'write_question_set_annotate',
        },
        READ_ANNOTATE: {
            [SECTION]: 'read_content_block_annotate',
            [QUESTION]: 'read_question_annotate',
            [QUESTION_SET]: 'read_question_set_annotate',
        },
    },
    [FeatureTypesWithCurs.ITEM_REVIEW_METADATA]: {
        WRITE_ITEM_REVIEW_METADATA: {
            [SECTION]: 'write_item_review_metadata',
        },
        READ_ITEM_REVIEW_METADATA: {
            [SECTION]: 'read_item_review_metadata',
        },
    },
    [FeatureTypesWithCurs.METADATA]: {
        WRITE_METADATA: {
            [SECTION]: 'write_content_block_metadata',
        },
    },
    [FeatureTypesWithCurs.ITEM_REVIEW_ADDITIONAL_RESOURCES]: {
        ITEM_REVIEW_ADDITIONAL_RESOURCES: {
            [SECTION]: 'read_item_review_additional_resources',
        },
    },
    [FeatureTypesWithCurs.CONTENT_BLOCK_AUDIO_SCRIPT]: {
        READ_CONTENT_BLOCK_AUDIO_SCRIPT: {
            [SECTION]: 'read_content_block_audio_script',
        },
    },
    [FeatureTypesWithCurs.ITEM_REVIEW_STATES]: {
        READ_ITEM_REVIEW_STATS: {
            [SECTION]: 'read_item_review_stats',
        },
    },
    [FeatureTypesWithCurs.ATC_CONTENT_BLOCKS_USAGE_LISTS]: {
        READ_ATC_CONTENT_BLOCKS_USAGE_LISTS: {
            [SECTION]: 'read_atc_content_blocks_usage_lists',
        },
    },
    [FeatureTypesWithCurs.CONTENT_BLOCK_EDIT]: {
        CONTENT_BLOCK_EDIT: {
            [QUESTION]: 'write_interaction_block',
            [SECTION]: 'write_content_block',
            [SECTION_TEMPLATE]: 'write_content_block_template',
            [QUESTION_SET]: 'write_question_set',
            [QUESTION_TEMPLATE]: 'write_interaction_block_templates',
            [QUESTION_SET_TEMPLATE]: 'write_question_set_template',
        },
    },
    [FeatureTypesWithCurs.CONTENT_BLOCK_READ]: {
        CONTENT_BLOCK_READ: {
            [SECTION]: 'read_content_block',
            // IB-9685 only mapped SECTION, so HeaderRight resolved undefined for Question Sets and
            // disabled the whole Options menu for every CUR governed user. MajorVersionsTabs passes
            // ContentTypes.SECTION explicitly and is unaffected by this entry.
            [QUESTION_SET]: 'read_question_set',
        },
    },
    [FeatureTypesWithCurs.INTERACTION_BLOCK]: {
        INTERACTION_BLOCK: {
            [QUESTION]: 'read_interaction_block',
        },
    },
    [FeatureTypesWithCurs.EDIT_STATUS]: {
        EDIT_STATUS: {
            [SECTION]: 'write_content_block_status',
            [SECTION_TEMPLATE]: 'write_content_block_template',
            [QUESTION_SET]: 'write_question_set_status',
            [QUESTION_TEMPLATE]: 'write_interaction_block_templates',
            [QUESTION_SET_TEMPLATE]: 'write_question_set_template_status',
        },
    },
    [FeatureTypesWithCurs.CREATE_CONTENT]: {
        CREATE_CONTENT: {
            [QUESTION_TEMPLATE]: 'create_interaction_block_templates',
            [QUESTION]: 'create_interaction_block',
        },
    },
    [FeatureTypesWithCurs.CREATE_QUESTIONS_WITHIN_QUESTION_SET]: {
        CREATE_QUESTIONS_WITHIN_QUESTION_SET: {
            [QUESTION]: 'create_questions_within_question_set',
        },
    },
    [FeatureTypesWithCurs.QUESTION_SET_DESIGN]: {
        QUESTION_SET_DESIGN: {
            [QUESTION_SET]: 'write_question_set_design',
        },
    },
    [FeatureTypesWithCurs.WRITE_RESHARE_CONTENT]: {
        WRITE_RESHARE_CONTENT: {
            [QUESTION]: 'write_reshare_content',
            [SECTION]: 'write_reshare_content',
            [QUESTION_SET]: 'write_reshare_content',
            [SECTION_TEMPLATE]: 'write_reshare_content',
            [QUESTION_TEMPLATE]: 'write_reshare_content',
            [QUESTION_SET_TEMPLATE]: 'write_reshare_content',
        },
    },
    [FeatureTypesWithCurs.OWNER_SHARE_CONTENT]: {
        OWNER_SHARE_CONTENT: {
            [QUESTION]: 'write_owner_share_content',
            [SECTION]: 'write_owner_share_content',
            [QUESTION_SET]: 'write_owner_share_content',
            [SECTION_TEMPLATE]: 'write_owner_share_content',
            [QUESTION_TEMPLATE]: 'write_owner_share_content',
            [QUESTION_SET_TEMPLATE]: 'write_owner_share_content',
        },
    },
    [FeatureTypesWithCurs.ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS]: {
        READ_ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS: {
            [SECTION]: 'read_item_review_associated_calibration_test_events',
        },
    },
};

export type CustomPermissionOnContent = {
    customUserRoleId: number;
    granteeIds: number[];
}

export const ExtendedAccessFeatures = [FeatureTypesWithCurs.ANNOTATE_CONTENT, FeatureTypesWithCurs.ITEM_REVIEW_ADDITIONAL_RESOURCES,
    FeatureTypesWithCurs.ITEM_REVIEW_METADATA, FeatureTypesWithCurs.ITEM_REVIEW_STATES,
    FeatureTypesWithCurs.CONTENT_BLOCK_VERSION_HISTORY, FeatureTypesWithCurs.QUESTION_VERSION_HISTORY,
    FeatureTypesWithCurs.ITEM_REVIEW_ASSOCIATED_CALIBRATION_TEST_EVENTS];
